import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  AportacionInversion, Categoria, Cuenta, Inversion, Presupuesto, RetiroInversion,
  Transaccion, TransaccionRecurrente, ValoracionInversion,
} from '../types';
import { NOMBRE_APP } from '../config/marca';
import { guardarPDF } from './plataforma';
import { formatoEuros } from './numeros';
import {
  balance, comercios, fijosYVariables, filasPresupuestos, mayoresGastos, mesesAtras, periodoAnio, periodoMes,
  porCuenta, reales, seriePatrimonio,
} from './informes';

/** Lo que necesita el informe completo; es un subconjunto de useData(). */
export interface DatosInformeCompleto {
  transacciones: Transaccion[];
  categorias: Categoria[];
  cuentas: Cuenta[];
  presupuestos: Presupuesto[];
  transaccionesRecurrentes: TransaccionRecurrente[];
  inversiones: Inversion[];
  aportacionesInversion: AportacionInversion[];
  retirosInversion: RetiroInversion[];
  valoracionesInversion: ValoracionInversion[];
}

const FIORDO: [number, number, number] = [62, 92, 118];
const GRIS: [number, number, number] = [241, 245, 249];

/**
 * C5 (Plus): PDF con todos los informes de un mes o un año: balance,
 * evolución, categorías, presupuestos, cuentas, patrimonio, fijos y los
 * mayores gastos. Las tablas son las mismas cuentas que la pantalla.
 */
export function exportarInformeCompletoPDF(
  d: DatosInformeCompleto,
  vista: { anio: number; mes: number | null },
  etiqueta: string,
): void {
  const { anio, mes } = vista;
  const periodo = mes === null ? periodoAnio(anio) : periodoMes(anio, mes);
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  let y = 0;

  const titulo = (texto: string) => {
    if (y > 250) { doc.addPage(); y = 16; }
    doc.setTextColor(30, 30, 30);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(texto, 14, y);
    y += 3;
  };
  const tabla = (head: string[], body: string[][], derecha = true) => {
    autoTable(doc, {
      startY: y,
      head: [head],
      body,
      headStyles: { fillColor: FIORDO, textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      alternateRowStyles: { fillColor: GRIS },
      columnStyles: derecha ? Object.fromEntries(head.slice(1).map((_, i) => [i + 1, { halign: 'right' as const }])) : {},
      margin: { left: 14, right: 14 },
    });
    y = (doc as any).lastAutoTable.finalY + 10;
  };

  // Cabecera
  doc.setFillColor(...FIORDO);
  doc.rect(0, 0, 210, 30, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(`${NOMBRE_APP} — Informe de ${etiqueta}`, 14, 13);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Sin traspasos ni adelantos: solo lo que de verdad entra y sale.', 14, 23);
  doc.text(`Generado: ${new Date().toLocaleDateString('es-ES')}`, 148, 23);
  y = 40;

  // Balance
  const b = balance(d.transacciones, periodo);
  titulo('Balance');
  tabla(['', 'Importe'], [
    ['Ingresos', formatoEuros(b.ingresos)],
    ['Gastos', formatoEuros(b.gastos)],
    ['Ahorro', formatoEuros(b.ahorro, true)],
    ['Tasa de ahorro', b.tasa === null ? '—' : `${Math.round(b.tasa)} %`],
  ]);

  // Evolución: los meses del año, o los 12 meses hasta el elegido
  titulo(mes === null ? `Mes a mes en ${anio}` : 'Últimos 12 meses');
  const meses: [number, number][] = mes === null
    ? Array.from({ length: 12 }, (_, i) => [anio, i + 1] as [number, number])
    : Array.from({ length: 12 }, (_, i) => mesesAtras(anio, mes, 11 - i));
  tabla(['Mes', 'Ingresos', 'Gastos', 'Ahorro'], meses.map(([a, m]) => {
    const x = balance(d.transacciones, periodoMes(a, m));
    return [new Date(a, m - 1, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }),
      formatoEuros(x.ingresos), formatoEuros(x.gastos), formatoEuros(x.ahorro, true)];
  }));

  // Categorías
  (['gasto', 'ingreso'] as const).forEach(tipo => {
    const txs = reales(d.transacciones, periodo, tipo);
    const total = txs.reduce((s, t) => s + t.cantidad, 0);
    const filas = d.categorias
      .filter(c => c.tipo === tipo)
      .map(c => ({ c, v: txs.filter(t => t.categoriaId === c.id).reduce((s, t) => s + t.cantidad, 0) }))
      .filter(x => x.v > 0)
      .sort((a, b2) => b2.v - a.v);
    if (!filas.length) return;
    titulo(tipo === 'gasto' ? 'Gastos por categoría' : 'Ingresos por categoría');
    tabla(['Categoría', 'Importe', '%'], [
      ...filas.map(x => [x.c.nombre, formatoEuros(x.v), `${Math.round((x.v / total) * 100)} %`]),
      ['Total', formatoEuros(total), '100 %'],
    ]);
  });

  // Presupuestos
  const pres = filasPresupuestos(d.presupuestos, d.transacciones, anio, mes);
  if (pres.length) {
    titulo('Presupuestos');
    tabla(['Presupuesto', 'Gastado', 'Límite', '%'], pres.map(f => [
      `${f.presupuesto.nombre}${f.presupuesto.periodicidad === 'anual' ? ' (anual)' : ''}`,
      formatoEuros(f.gastado), formatoEuros(f.limite), `${Math.round(f.porcentaje)} %`,
    ]));
  }

  // Cuentas
  const cuentas = porCuenta(d.transacciones, d.cuentas, periodo);
  if (cuentas.length) {
    titulo('Por cuenta y tarjeta');
    tabla(['Cuenta', 'Gastos', 'Ingresos'], cuentas.map(f => [f.cuenta.nombre, formatoEuros(f.gastos), formatoEuros(f.ingresos)]));
  }

  // Patrimonio a fin de periodo (como mucho, hasta el mes actual)
  const hoy = new Date();
  let [aFin, mFin] = mes === null ? [anio, 12] : [anio, mes];
  if (aFin > hoy.getFullYear() || (aFin === hoy.getFullYear() && mFin > hoy.getMonth() + 1)) {
    [aFin, mFin] = [hoy.getFullYear(), hoy.getMonth() + 1];
  }
  const [p] = seriePatrimonio(d.cuentas, d.transacciones, {
    inversiones: d.inversiones, aportaciones: d.aportacionesInversion,
    retiros: d.retirosInversion, valoraciones: d.valoracionesInversion,
  }, aFin, mFin, 1);
  titulo('Patrimonio');
  tabla(['', 'Importe'], [
    ['Cuentas', formatoEuros(p.cuentas)],
    ['Huchas', formatoEuros(p.huchas)],
    ['Inversiones', formatoEuros(p.inversiones)],
    ['Total', formatoEuros(p.total)],
  ]);

  // Fijos y variables
  const fv = fijosYVariables(d.transacciones, d.transaccionesRecurrentes, periodo);
  if (fv.fijos + fv.variables > 0) {
    titulo('Gastos fijos y variables');
    tabla(['', 'Importe', '%'], [
      ['Fijos (periódicos)', formatoEuros(fv.fijos), fv.porcentajeFijos === null ? '' : `${Math.round(fv.porcentajeFijos)} %`],
      ['Variables', formatoEuros(fv.variables), fv.porcentajeFijos === null ? '' : `${Math.round(100 - fv.porcentajeFijos)} %`],
    ]);
  }

  // Mayores gastos y comercios
  const top = mayoresGastos(d.transacciones, periodo);
  if (top.length) {
    const cat = (id: string) => d.categorias.find(c => c.id === id)?.nombre ?? '';
    titulo('Los gastos más grandes');
    tabla(['Fecha', 'Descripción', 'Categoría', 'Importe'], top.map(t => [
      new Date(t.fecha).toLocaleDateString('es-ES'), t.descripcion || '—', cat(t.categoriaId), formatoEuros(t.cantidad),
    ]), false);
  }
  const rep = comercios(d.transacciones, periodo);
  if (rep.length) {
    titulo('Dónde repites');
    tabla(['Descripción', 'Veces', 'Total'], rep.map(c => [c.nombre, String(c.veces), formatoEuros(c.total)]));
  }

  guardarPDF(doc, `informe_${mes === null ? anio : `${anio}-${String(mes).padStart(2, '0')}`}.pdf`);
}
